import React, {
    useCallback, useMemo, useRef, useState,
} from 'react';
import {
    Coins, AlertCircle, AlertTriangle, CheckCircle, History, RotateCcw,
} from 'lucide-react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackEvent, trackException, getCurrentTraceId } from '../services/appInsightsService';
import './UsageLedgerSection.css';

/**
 * UsageLedgerSection — Admin Settings > Usage Ledger. The operator surface over ONE tenant's
 * usage-credit ledger: read it, preview an adjustment, apply one.
 *
 * Sprint 13 of plans/Credit-Pricing-Redesign-Plan.md, against the contract Sprint 11 actually
 * delivered in `src/Functions/Functions/Admin/UsageLedgerAdminFunctions.cs` and
 * `src/Functions/Services/Billing/UsageLedgerAdminService.cs`:
 *
 *   GET  /platform/tenants/{tenantId}/usage-ledger?cursor=&limit=     role >= 3
 *   POST /platform/tenants/{tenantId}/usage-adjustments/preview       role >= 4, writes nothing
 *   POST /platform/tenants/{tenantId}/usage-adjustments               role >= 4
 *
 * This screen moves money, so five delivered-contract facts shape it directly. Each one is a
 * rule about what NOT to build, and each is here because the alternative creates real credit:
 *
 *   1. THE REFUSAL CODE IS KEYED BY THE VERSION CARRIER (`CheckVersion`): no version at all is
 *      428, an `If-Match` mismatch is 412, a body `balanceVersion` mismatch is 409. This page
 *      always sends the version as `If-Match` and never in the body, so its stale-version
 *      answer is 412. Sending neither would be a write with no optimistic check at all.
 *   2. `ticketId` in the BODY — not the `Idempotency-Key` header — is the ledger identity: it
 *      is the last segment of the grant and operation ids, so it is what Cosmos itself
 *      deduplicates on. A replay answers 200 with `alreadyProcessed: true` and an UNCHANGED
 *      balance. The header is sent as well, recorded but not authoritative.
 *   3. NOTHING IN THIS UI MAY EVER SUGGEST MINTING A NEW TICKET. A new ticket is a new ledger
 *      identity, so "try again with a fresh ticket" is the instruction that writes a second
 *      real credit. Every refusal here says to re-read the ledger and repeat with the SAME
 *      ticket.
 *   4. ONLY TWO EXPIRY POLICIES EXIST. `none` and `custom`. The third one the wireframe drew
 *      was withdrawn in Sprint 11 (400 QUOTA_PERIOD_POLICY_UNAVAILABLE) because it re-derived
 *      the expiry on every attempt, so an ordinary retry after a client timeout tripped the
 *      replay fence on a byte-identical request.
 *   5. SEAT-SCOPED ADJUSTMENTS ARE REFUSED (400 SEAT_SCOPED_ADJUSTMENT_UNSUPPORTED), because
 *      an `adjust:` grant id is not a shape `UsageGrantIdentity.TryGetSeatUserId` recognises,
 *      so the stored grant would carry no seat and the allocator would spend it as the tenant
 *      pool — "give THIS seat 50 points" silently becoming "give the tenant 50 points". So no
 *      seat control exists here, and `userId` never goes on the wire.
 *
 * The optimistic lock is a CHECK, NOT A FENCE (sprint-11.md §5): the comparison happens in the
 * service and the write happens afterwards in the ledger, and no wallet filter looks at
 * `usageBalanceVersion`. Two operators holding DIFFERENT tickets can both pass it and both
 * write. The page therefore always sends the version it read AND says so on screen, rather
 * than presenting the check as a guarantee it is not.
 *
 * Points are the operator's unit and milli is the ledger's: 1 point = 1000 milli (D3.1 §1.4).
 * Every figure renders both — the rounded points to read, the exact milli to reconcile against
 * a ledger document.
 *
 * Every table wrapper carries `role="region"`, a name and `tabIndex={0}`: these tables scroll
 * horizontally so a 45-character grant id never has to wrap, and a region that scrolls but
 * cannot be focused is unreachable by keyboard (axe `scrollable-region-focusable`).
 *
 * English copy only (AdminPortal's recorded localization exception) — this portal carries no
 * i18n framework.
 */

/** 1 point = 1000 milli (D3.1 §1.4). The wire is milli; the operator reads and types points. */
const MILLI_PER_POINT = 1000;

/** Level required to READ a tenant's ledger; matches UsageLedgerAdminFunctions.cs. */
const MINIMUM_READ_LEVEL = 3;

/** Level required to ADJUST a tenant's credits; matches UsageLedgerAdminFunctions.cs. */
const MINIMUM_WRITE_LEVEL = 4;

/** The server's own reason bounds (UsageLedgerAdminService.cs:64, :67). */
const MIN_REASON_LENGTH = 5;
const MAX_REASON_LENGTH = 500;

/** `UsageLedgerAdminService.TicketPattern` — the ticket is interpolated into two Cosmos ids,
 *  and Cosmos rejects / \ ? # in an id, so a ticket this page let through would preview
 *  cleanly and then fail the write with an empty error. */
const TICKET_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

/** Grants inside this window are called out by their own TEXT, never by colour alone. */
const EXPIRY_SOON_DAYS = 7;

/**
 * `CustomerConstants.GetPlatformRoleLevel`, mirrored for the client-side render decision only.
 * The SERVER is the enforcer — this map decides whether a control is worth showing, never
 * whether a write is allowed.
 */
const PLATFORM_ROLE_LEVELS = {
    SuperAdmin: 5,
    PlatformContributor: 4,
    PlatformReadOnly: 3,
    Unlimited: 2,
};

const roleLevelOf = (role) => PLATFORM_ROLE_LEVELS[role] || 0;

/** The only real production Functions host — everything else reads as Staging, so a
 *  misconfigured environment can never be mistaken for production. */
const isProductionHost = (baseUrl) => {
    try {
        return new URL(baseUrl).hostname.toLowerCase() === 'func.qolsync.ai';
    } catch (e) {
        return false;
    }
};

/** Never send an empty X-Trace-Id — a money write that arrives uncorrelated cannot be tied back
 *  to its server-side audit entry, and the audit entry is all this ledger stores about "why". */
const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;

const pointsFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

/** Points, rounded for reading. Never the value anything is computed from. */
const toPoints = (milli) => pointsFormatter.format(Number(milli || 0) / MILLI_PER_POINT);

/** Points are fractional (`meeting.extraParticipantMinute` is 0.3 points), so this parses a
 *  float and rounds only at the milli boundary. A `parseInt` here would turn 0.3 into 0. */
const toMilli = (pointsText) => {
    const points = Number.parseFloat(pointsText);
    return Number.isFinite(points) ? Math.round(points * MILLI_PER_POINT) : Number.NaN;
};

const signedPoints = (milli) => `${Number(milli) >= 0 ? '+' : '-'}${toPoints(Math.abs(Number(milli || 0)))}`;

const formatUtc = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return `${date.toISOString().slice(0, 16).replace('T', ' ')}Z`;
};

const daysUntil = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return Math.ceil((date.getTime() - Date.now()) / 86400000);
};

const formatAge = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

/** Error bodies on this surface are `{ errorCode, error }` — NEVER `{ code, message }`. Reading
 *  `body.message` would render `undefined` and hide the server's real explanation. */
const errorTextOf = (body, fallback) => (body && body.error) || fallback;

/**
 * The `Idempotency-Key` header value.
 *
 * Deliberately DERIVED from the payload rather than randomly generated once: the operator's
 * real retry is "I am not sure the first one landed, send it again", and a key that changed
 * between those two attempts would make the header disagree with the ticket that actually
 * carries the identity. The ticket is still the authority; this only has to be stable for the
 * same figures.
 */
const idempotencyKeyFor = (form) => {
    const ticket = (form.ticket || '').trim();
    if (!ticket) return '';
    const amountMilli = toMilli(form.points);
    const amount = Number.isFinite(amountMilli) ? amountMilli : 0;
    return `adj-${form.kind}-${ticket}-${amount}`;
};

const EMPTY_FORM = {
    kind: 'compensate',
    points: '',
    grantId: '',
    expiryPolicy: 'none',
    expiresAtUtc: '',
    reason: '',
    ticket: '',
};

const UsageLedgerSection = ({ role = '', actor = '', tenants = [] }) => {
    const [tenantInput, setTenantInput] = useState('');
    const [tenantId, setTenantId] = useState('');
    const [ledger, setLedger] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [preview, setPreview] = useState(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [adjustError, setAdjustError] = useState(null);
    const [adjustResult, setAdjustResult] = useState(null);
    const [sessionAudit, setSessionAudit] = useState([]);

    // Defence in depth behind the button's own `disabled={isApplying …}`, which is the gate a
    // test can observe (measured: reverting THAT sends a second request; reverting this ref
    // sends none, because React 18 flushes a discrete click synchronously and the second click
    // already meets a disabled button). It is kept for the paths that do not go through that
    // button at all — a handler re-entered before the commit that disables it.
    const applyingRef = useRef(false);

    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);
    const envLabel = isProductionHost(target.baseUrl) ? 'Production' : 'Staging';

    const roleLevel = roleLevelOf(role);
    const canRead = roleLevel >= MINIMUM_READ_LEVEL;
    const canWrite = roleLevel >= MINIMUM_WRITE_LEVEL;

    const ledgerUrl = useCallback(
        (id, cursor) => `${target.baseUrl}/platform/tenants/${encodeURIComponent(id)}/usage-ledger${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
        [target.baseUrl],
    );

    const loadLedger = useCallback(async (id) => {
        // An unresolved target is the hidden staging fallback, not a deliberate backend — do
        // not send this console's JWT to it (same guard as RealtimeProviderPolicySettings.js).
        if (!target.resolved || !id) return null;

        setIsLoading(true);
        try {
            const response = await fetchWithAuth(ledgerUrl(id), {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                const error = new Error(errorTextOf(body, `Failed to load the usage ledger (HTTP ${response.status}).`));
                error.errorCode = body.errorCode;
                error.status = response.status;
                throw error;
            }
            const payload = await response.json();
            if (!payload || !payload.balance) {
                throw new Error('The usage-ledger response did not include a balance block.');
            }
            setLedger(payload);
            setLoadErrorMessage(null);
            trackEvent('UsageLedgerLoaded', {
                tenantId: id,
                balanceVersion: payload.balance.balanceVersion,
                grantCount: (payload.grants || []).length,
                stuckReservationCount: (payload.stuckReservations || []).length,
            });
            return payload;
        } catch (err) {
            trackException(err, {
                errorType: 'UsageLedger_LoadFailed', errorCode: err.errorCode, status: err.status,
            });
            // A failed load renders NO ledger. A partial or default view here would present
            // figures nobody read as if they were this tenant's, on a screen whose next control
            // creates credit.
            setLedger(null);
            setLoadErrorMessage(err.message || 'Failed to load the usage ledger.');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [ledgerUrl, target.resolved]);

    const handleSearch = async () => {
        const id = tenantInput.trim();
        if (!target.resolved || !id || isLoading) return;
        setTenantId(id);
        setPreview(null);
        setAdjustError(null);
        setAdjustResult(null);
        await loadLedger(id);
    };

    /**
     * A reload REPLACES the balance and its version, so any standing preview has to go with it.
     * Leaving it is worse than merely stale: `balanceVersion` is re-derived from the response,
     * so the `If-Match` a still-enabled Confirm would send is the FRESH version and the 412
     * that is otherwise the only warning never fires — the operator confirms a money write
     * while reading figures the server has already replaced.
     */
    const handleReload = async () => {
        if (!tenantId || isLoading) return;
        setPreview(null);
        setAdjustError(null);
        setAdjustResult(null);
        await loadLedger(tenantId);
    };

    const loadMoreOperations = async () => {
        const cursor = ledger && ledger.operations && ledger.operations.nextCursor;
        if (!cursor || !tenantId || isLoading) return;
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(ledgerUrl(tenantId, cursor), {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(errorTextOf(body, `Failed to load more operations (HTTP ${response.status}).`));
            }
            const payload = await response.json();
            // Append, never replace: the earlier pages are the operator's scroll position and
            // the reason they asked for more.
            setLedger((prev) => (prev ? {
                ...prev,
                operations: {
                    items: [...(prev.operations.items || []), ...((payload.operations && payload.operations.items) || [])],
                    nextCursor: (payload.operations && payload.operations.nextCursor) || null,
                },
            } : prev));
        } catch (err) {
            trackException(err, { errorType: 'UsageLedger_LoadMoreFailed' });
            setLoadErrorMessage(err.message || 'Failed to load more operations.');
        } finally {
            setIsLoading(false);
        }
    };

    /** Every edit invalidates the preview: the figures on screen must always describe exactly
     *  what Confirm would send, and a preview that outlives the form it described is the way a
     *  UI comes to believe it is still previewing while money moves. */
    const updateForm = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
        setPreview(null);
        setAdjustError(null);
    };

    const balance = ledger && ledger.balance;
    const balanceVersion = balance ? balance.balanceVersion : null;
    const grants = (ledger && ledger.grants) || [];
    const clawbackTargets = grants.filter(
        (grant) => grant.usageRemainingMilli > 0 && (!grant.expiresAt || new Date(grant.expiresAt).getTime() > Date.now()),
    );

    const amountMilli = toMilli(form.points);
    const reasonText = (form.reason || '').trim();
    const ticketText = (form.ticket || '').trim();
    const isClawback = form.kind === 'clawback';
    const clawbackTarget = clawbackTargets.find((grant) => grant.id === form.grantId) || null;

    const amountValid = Number.isFinite(amountMilli) && amountMilli > 0;
    const reasonValid = reasonText.length >= MIN_REASON_LENGTH && (form.reason || '').length <= MAX_REASON_LENGTH;
    const ticketValid = TICKET_PATTERN.test(ticketText);
    const expiryValid = form.expiryPolicy !== 'custom'
        || (!!form.expiresAtUtc && new Date(form.expiresAtUtc).getTime() > Date.now());
    const targetValid = !isClawback || !!clawbackTarget;
    const withinRemaining = !isClawback || !clawbackTarget || amountMilli <= clawbackTarget.usageRemainingMilli;
    const formValid = amountValid && reasonValid && ticketValid && expiryValid && targetValid && withinRemaining;

    const idempotencyKey = idempotencyKeyFor(form);

    const buildBody = (confirm) => {
        const body = {
            kind: form.kind,
            amountMilli,
            reason: reasonText,
            ticketId: ticketText,
            confirm,
        };
        if (isClawback) {
            body.grantId = form.grantId;
        } else {
            // Only two policies exist. `quotaPeriod` is a 400, and its refusal text used to
            // invite a second real credit.
            body.expiryPolicy = form.expiryPolicy === 'custom' ? 'custom' : 'none';
            if (body.expiryPolicy === 'custom') {
                body.expiresAtUtc = new Date(form.expiresAtUtc).toISOString();
            }
        }
        // `userId` is never set: the server refuses a seat-scoped adjustment, and a control
        // that could set it would silently turn a seat grant into a tenant-pool grant.
        return body;
    };

    /** Adds the one sentence a refusal must always end with. Never "use a new ticket". */
    const withRetryGuidance = (message) => `${message} Reload the ledger, check whether this `
        + `adjustment already landed, then repeat it with the same ticket id.`;

    const handlePreview = async () => {
        if (!canWrite || !ledger || !formValid || isPreviewing || isApplying) return;
        setAdjustError(null);
        // Dropped BEFORE the request, not merely replaced after a successful one: a re-preview
        // the server refuses used to leave the previous projection and its live Confirm button
        // sitting beside the refusal banner.
        setPreview(null);
        setIsPreviewing(true);
        try {
            const response = await fetchWithAuth(
                `${target.baseUrl}/platform/tenants/${encodeURIComponent(tenantId)}/usage-adjustments/preview`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'If-Match': String(balanceVersion),
                        'X-Trace-Id': traceIdHeader(),
                    },
                    body: JSON.stringify(buildBody(false)),
                },
            );
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                const message = errorTextOf(body, `Failed to preview the adjustment (HTTP ${response.status}).`);
                trackException(new Error(message), {
                    errorType: 'UsageLedger_PreviewFailed', errorCode: body.errorCode, status: response.status,
                });
                setAdjustError(response.status === 412 || response.status === 428
                    ? withRetryGuidance(message) : message);
                return;
            }
            setPreview(body);
            // No raw ticket, operation id or idempotency key in telemetry. The server digests
            // exactly those four identities before publishing its own event
            // (`UsageLedgerAdminService.TrackAdjustment` -> `IdentityLogHelper.Digest`) and
            // leaves only the tenant id in clear; a client event that republished them in
            // cleartext would undo that redaction from the other end. The auto-enriched
            // traceId already ties this event to the server's, which carries the digests.
            trackEvent('UsageAdjustmentPreviewed', {
                tenantId, kind: form.kind, amountMilli, balanceVersion, ticketLength: ticketText.length,
            });
        } catch (err) {
            trackException(err, { errorType: 'UsageLedger_PreviewThrew' });
            setAdjustError(err.message || 'Failed to preview the adjustment.');
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleConfirm = async () => {
        if (!canWrite || !ledger || !preview || !formValid) return;
        if (applyingRef.current) return;
        applyingRef.current = true;
        setAdjustError(null);
        setIsApplying(true);
        try {
            const response = await fetchWithAuth(
                `${target.baseUrl}/platform/tenants/${encodeURIComponent(tenantId)}/usage-adjustments`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'If-Match': String(balanceVersion),
                        'Idempotency-Key': idempotencyKey,
                        'X-Trace-Id': traceIdHeader(),
                    },
                    body: JSON.stringify(buildBody(true)),
                },
            );
            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                const message = errorTextOf(body, `Failed to apply the adjustment (HTTP ${response.status}).`);
                trackException(new Error(message), {
                    errorType: 'UsageLedger_AdjustRefused', errorCode: body.errorCode, status: response.status,
                });
                // 412/428/409 all mean "nothing was written under these figures". The ledger is
                // re-read for the two version carriers, because a stale precondition repeats
                // forever until the version on screen is the server's.
                setAdjustError(response.status === 409 ? message : withRetryGuidance(message));
                setPreview(null);
                if (response.status === 412 || response.status === 428) await loadLedger(tenantId);
                return;
            }

            const entry = {
                atUtc: new Date().toISOString(),
                actor: actor || '-',
                kind: form.kind,
                appliedMilli: body.appliedMilli,
                ticketId: ticketText,
                operationId: body.operationId,
                grantId: body.grantId,
                idempotencyKey,
                alreadyProcessed: !!body.alreadyProcessed,
            };
            setAdjustResult(entry);
            setSessionAudit((prev) => [entry, ...prev]);
            setPreview(null);
            // Same redaction rule as the preview event above: figures yes, identities no.
            trackEvent('UsageAdjustmentApplied', {
                tenantId,
                kind: form.kind,
                appliedMilli: body.appliedMilli,
                ticketLength: ticketText.length,
                alreadyProcessed: !!body.alreadyProcessed,
            });
            await loadLedger(tenantId);
        } catch (err) {
            trackException(err, { errorType: 'UsageLedger_AdjustThrew' });
            setAdjustError(err.message || 'Failed to apply the adjustment.');
        } finally {
            applyingRef.current = false;
            setIsApplying(false);
        }
    };

    const notice = (tone, Icon, children, testId, liveRole) => (
        <div
            className={`usage-ledger-notice usage-ledger-notice-${tone}`}
            role={liveRole}
            data-testid={testId}
        >
            <Icon size={16} className="usage-ledger-notice-icon" style={{ color: `var(--${tone === 'info' ? 'text-secondary' : tone})` }} aria-hidden="true" />
            <span>{children}</span>
        </div>
    );

    const stuck = (ledger && ledger.stuckReservations) || [];
    const malformed = (ledger && ledger.malformedGrants) || [];
    const operations = (ledger && ledger.operations && ledger.operations.items) || [];
    const seats = (ledger && ledger.seats) || [];
    // S11b. Defaulted rather than assumed: an older backend that does not send the block must
    // degrade the identity row, never crash the screen an operator opened because something
    // already looked wrong.
    const tenant = (ledger && ledger.tenant) || {};

    return (
        <section className="usage-ledger-section" aria-labelledby="usage-ledger-title">
            <div className="usage-ledger-header">
                <div className="usage-ledger-header-left">
                    <Coins size={20} aria-hidden="true" />
                    <div>
                        <h2 id="usage-ledger-title">Tenant ledger</h2>
                        <p>
                            One tenant&apos;s usage-credit balance, grants and operations. Adjustments are
                            written as new ledger operations — never as a field overwrite — and always in
                            two steps, preview then confirm.
                        </p>
                    </div>
                </div>
                <div className="usage-ledger-chips">
                    <span className="usage-ledger-env-chip">{envLabel}</span>
                    {balance && (
                        <span className="usage-ledger-chip" data-testid="usage-ledger-balance-version">
                            {`balanceVersion ${balance.balanceVersion}`}
                        </span>
                    )}
                </div>
            </div>

            {!canRead && (
                <div className="usage-ledger-card">
                    {notice('warning', AlertTriangle, 'This ledger needs platform role level 3 or above.', 'usage-ledger-denied', 'status')}
                </div>
            )}

            {canRead && (
                <>
                    <div className="usage-ledger-card">
                        <div className="usage-ledger-search">
                            <div className="usage-ledger-field">
                                <label htmlFor="usage-ledger-tenant">Tenant id</label>
                                {/* ponytail: native <datalist> — the browser owns the filtering and
                                    the popup, so match rules vary by browser and it cannot be
                                    styled. Swap for a custom combobox if that starts to matter. */}
                                <input
                                    id="usage-ledger-tenant"
                                    type="text"
                                    list="usage-ledger-tenant-options"
                                    value={tenantInput}
                                    onChange={(e) => setTenantInput(e.target.value)}
                                    disabled={!target.resolved || isLoading}
                                    placeholder="Search by tenant name or id"
                                    autoComplete="off"
                                />
                                <datalist id="usage-ledger-tenant-options">
                                    {[...tenants]
                                        .sort((a, b) => (a.TenantName || a.RowKey || '').localeCompare(b.TenantName || b.RowKey || ''))
                                        .map((t) => <option key={t.RowKey} value={t.RowKey}>{t.TenantName || ''}</option>)}
                                </datalist>
                                <small>
                                    Pick a tenant from the list or paste its id. A tenant with no usage
                                    wallet answers 404 rather than a zero balance.
                                </small>
                            </div>
                            <button
                                type="button"
                                className="usage-ledger-primary-btn"
                                onClick={handleSearch}
                                disabled={!target.resolved || isLoading || !tenantInput.trim()}
                            >
                                {isLoading ? 'Loading…' : 'Load ledger'}
                            </button>
                            {ledger && (
                                <button
                                    type="button"
                                    className="usage-ledger-secondary-btn"
                                    onClick={handleReload}
                                    disabled={isLoading}
                                >
                                    <RotateCcw size={16} aria-hidden="true" />
                                    Reload
                                </button>
                            )}
                        </div>
                        {!target.resolved && (
                            <p className="usage-ledger-hint" style={{ marginTop: 'var(--space-3)' }}>
                                No Functions endpoint is configured for this console, so nothing is fetched.
                            </p>
                        )}
                    </div>

                    {loadErrorMessage && notice('error', AlertCircle, loadErrorMessage, 'usage-ledger-load-error', 'alert')}

                    {ledger && balance && (
                        <>
                            <div className="usage-ledger-card">
                                <h3>Tenant</h3>
                                <dl className="usage-ledger-meta">
                                    <div>
                                        <dt>Tenant id</dt>
                                        <dd><code>{tenantId}</code></dd>
                                    </div>
                                    <div>
                                        <dt>Billing epoch</dt>
                                        <dd>{balance.billingEpoch}</dd>
                                    </div>
                                    <div>
                                        <dt>Balance version</dt>
                                        <dd>{balance.balanceVersion}</dd>
                                    </div>
                                    <div>
                                        <dt>Seats with a monthly grant</dt>
                                        <dd>{seats.length}</dd>
                                    </div>
                                </dl>
                                <dl className="usage-ledger-meta" data-testid="usage-ledger-tenant-identity">
                                    <div>
                                        <dt>Plan</dt>
                                        <dd>{tenant.planId ? <code>{tenant.planId}</code> : '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Line</dt>
                                        <dd>{tenant.line || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Channel</dt>
                                        <dd>{tenant.channel || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Quota period</dt>
                                        <dd>{tenant.quotaPeriodId || '—'}</dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="usage-ledger-card">
                                <h3>Balance</h3>
                                <div className="usage-ledger-kpis" data-testid="usage-ledger-balance">
                                    {[
                                        ['Monthly', 'monthlyMilli'],
                                        ['Purchased', 'purchasedMilli'],
                                        ['Admin adjustment', 'adminAdjustmentMilli'],
                                        ['Reserved', 'reservedMilli'],
                                        ['Available', 'availableMilli'],
                                    ].map(([label, field]) => (
                                        <div className="usage-ledger-kpi" key={field} data-testid={`balance-${field}`}>
                                            <span className="usage-ledger-kpi-label">{label}</span>
                                            <span className="usage-ledger-kpi-value">{`${toPoints(balance[field])} points`}</span>
                                            <span className="usage-ledger-kpi-raw">{`${balance[field]} milli`}</span>
                                        </div>
                                    ))}
                                </div>
                                <p className="usage-ledger-hint" style={{ marginTop: 'var(--space-3)' }}>
                                    Available is what the allocator can spend; reserved is held by live
                                    sessions and is not part of it.
                                </p>
                            </div>

                            {malformed.length > 0 && (
                                <div className="usage-ledger-card">
                                    <div className="usage-ledger-notice usage-ledger-notice-error" role="alert" data-testid="usage-ledger-malformed">
                                        <AlertCircle size={16} className="usage-ledger-notice-icon" style={{ color: 'var(--error)' }} aria-hidden="true" />
                                        <div>
                                            <strong>{`${malformed.length} stored grant row(s) fail the ledger's own invariants.`}</strong>
                                            The ledger refuses to operate on these rows, so their points are
                                            not in the balance above.
                                            <ul>
                                                {malformed.map((row) => (
                                                    <li key={row.id}>
                                                        <code>{row.id}</code>
                                                        {` — ${row.error}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {stuck.length > 0 && (
                                <div className="usage-ledger-card">
                                    <div className="usage-ledger-notice usage-ledger-notice-warning" role="alert" data-testid="usage-ledger-stuck">
                                        <AlertTriangle size={16} className="usage-ledger-notice-icon" style={{ color: 'var(--warning)' }} aria-hidden="true" />
                                        <div>
                                            <strong>{`${stuck.length} reservation(s) have outlived twice the hold window.`}</strong>
                                            This is a diagnosis, not a control: releasing a reservation is the
                                            session-end path&apos;s job, so nothing here releases one.
                                            <ul>
                                                {stuck.map((row) => (
                                                    <li key={row.operationId}>
                                                        <code>{row.operationId}</code>
                                                        {` — held ${formatAge(row.ageSeconds)}, taken ${formatUtc(row.createdAt)}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="usage-ledger-card">
                                <h3>Seats</h3>
                                {seats.length === 0 ? (
                                    <p className="usage-ledger-empty">No seat currently holds a monthly grant.</p>
                                ) : (
                                    <div className="usage-ledger-table-wrap" role="region" tabIndex={0} aria-label="Seats table, scrolls horizontally">
                                        <table className="usage-ledger-table" data-testid="usage-ledger-seats">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Seat</th>
                                                    <th scope="col">Grant</th>
                                                    <th scope="col" className="usage-ledger-num">Remaining</th>
                                                    <th scope="col">Expires (UTC)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {seats.map((seat) => (
                                                    <tr key={`${seat.userId}-${seat.grantId}`}>
                                                        <th scope="row">{seat.userId || '-'}</th>
                                                        <td><code>{seat.grantId}</code></td>
                                                        <td className="usage-ledger-num">
                                                            {`${toPoints(seat.remainingMilli)} points`}
                                                        </td>
                                                        <td>{formatUtc(seat.expiresAtUtc)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="usage-ledger-card">
                                <h3>Grants</h3>
                                {grants.length === 0 ? (
                                    <p className="usage-ledger-empty">This tenant holds no usage-credit grants.</p>
                                ) : (
                                    <div className="usage-ledger-table-wrap" role="region" tabIndex={0} aria-label="Grants table, scrolls horizontally">
                                        <table className="usage-ledger-table usage-ledger-table-wide" data-testid="usage-ledger-grants">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Grant</th>
                                                    <th scope="col">Kind</th>
                                                    <th scope="col">Seat</th>
                                                    <th scope="col" className="usage-ledger-num">Granted</th>
                                                    <th scope="col" className="usage-ledger-num">Remaining</th>
                                                    <th scope="col" className="usage-ledger-num">Reserved</th>
                                                    <th scope="col" className="usage-ledger-num">Consumed</th>
                                                    <th scope="col" className="usage-ledger-num">Clawed back</th>
                                                    <th scope="col">Expires (UTC)</th>
                                                    <th scope="col" className="usage-ledger-num">Epoch</th>
                                                    <th scope="col">Created (UTC)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {grants.map((grant) => {
                                                    const days = daysUntil(grant.expiresAt);
                                                    // An expired grant KEEPS a non-zero remaining figure in its stored
                                                    // document, but GetUsageCreditBalanceAsync excludes it from every
                                                    // bucket and a clawback against it is GRANT_NOT_FOUND. Unlabelled it
                                                    // reads as spendable points the Balance card simply failed to count.
                                                    const expired = days !== null && days < 0;
                                                    const soon = days !== null && days >= 0 && days <= EXPIRY_SOON_DAYS;
                                                    return (
                                                        <tr key={grant.id} className={soon || expired ? 'usage-ledger-flagged' : undefined}>
                                                            <th scope="row"><code>{grant.id}</code></th>
                                                            <td>{grant.grantKind}</td>
                                                            <td>{grant.userId || 'tenant pool'}</td>
                                                            <td className="usage-ledger-num">{toPoints(grant.usageGrantedMilli)}</td>
                                                            <td className="usage-ledger-num">{toPoints(grant.usageRemainingMilli)}</td>
                                                            <td className="usage-ledger-num">{toPoints(grant.usageReservedMilli)}</td>
                                                            <td className="usage-ledger-num">{toPoints(grant.usageConsumedMilli)}</td>
                                                            <td className="usage-ledger-num">{toPoints(grant.usageClawedBackMilli)}</td>
                                                            <td>
                                                                {grant.expiresAt ? formatUtc(grant.expiresAt) : 'never'}
                                                                {soon && ` (in ${days} day${days === 1 ? '' : 's'})`}
                                                                {expired && ' (expired — not counted in the balance)'}
                                                            </td>
                                                            <td className="usage-ledger-num">{grant.billingEpoch}</td>
                                                            <td>{formatUtc(grant.createdAt)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="usage-ledger-card">
                                <h3>Operations</h3>
                                {operations.length === 0 ? (
                                    <p className="usage-ledger-empty">No ledger operation has been written for this tenant.</p>
                                ) : (
                                    <div className="usage-ledger-table-wrap" role="region" tabIndex={0} aria-label="Operations table, scrolls horizontally">
                                        <table className="usage-ledger-table usage-ledger-table-wide" data-testid="usage-ledger-operations">
                                            <thead>
                                                <tr>
                                                    <th scope="col">At (UTC)</th>
                                                    <th scope="col">Operation</th>
                                                    <th scope="col">Kind</th>
                                                    <th scope="col">State</th>
                                                    <th scope="col" className="usage-ledger-num">Delta</th>
                                                    <th scope="col" className="usage-ledger-num">Applied</th>
                                                    <th scope="col" className="usage-ledger-num">Finalized</th>
                                                    <th scope="col">Rate snapshot</th>
                                                    <th scope="col" className="usage-ledger-num">Epoch</th>
                                                    <th scope="col">Related</th>
                                                    <th scope="col">Trace</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {operations.map((op) => (
                                                    <tr key={op.id}>
                                                        <th scope="row">{formatUtc(op.createdAt)}</th>
                                                        <td><code>{op.id}</code></td>
                                                        <td>{op.operationKind}</td>
                                                        <td>{op.usageState || '-'}</td>
                                                        <td className="usage-ledger-num">{signedPoints(op.usageDeltaMilli)}</td>
                                                        <td className="usage-ledger-num">{toPoints(op.usageAppliedMilli)}</td>
                                                        <td className="usage-ledger-num">{toPoints(op.usageFinalizedMilli)}</td>
                                                        <td>{op.rateSnapshot || '-'}</td>
                                                        <td className="usage-ledger-num">{op.billingEpoch}</td>
                                                        <td>{op.relatedOperationId ? <code>{op.relatedOperationId}</code> : '-'}</td>
                                                        <td>{op.traceId ? <code>{op.traceId}</code> : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <p className="usage-ledger-hint" style={{ marginTop: 'var(--space-3)' }}>
                                    Only the operator-adjustment rail records a trace id today, so a blank
                                    Trace cell on an older row is expected, not a gap.
                                </p>
                                {ledger.operations && ledger.operations.nextCursor && (
                                    <button
                                        type="button"
                                        className="usage-ledger-secondary-btn"
                                        onClick={loadMoreOperations}
                                        disabled={isLoading}
                                    >
                                        <History size={16} aria-hidden="true" />
                                        Load more operations
                                    </button>
                                )}
                            </div>

                            {canWrite && (
                                <div className="usage-ledger-card" data-testid="usage-ledger-adjust">
                                    <h3>Manual adjustment</h3>
                                    <p className="usage-ledger-hint">
                                        Every adjustment is a new ledger operation carrying the ticket as its
                                        identity, so sending the same ticket twice applies it once. The
                                        balance version you loaded is carried into the write itself: if the
                                        balance moves between your read and your confirm, the write is
                                        refused rather than applied. Two operators compensating the same
                                        incident under different tickets no longer both land — the second
                                        is told to re-read.
                                    </p>

                                    <div className="usage-ledger-adjust-grid">
                                        <div className="usage-ledger-adjust-form">
                                            <fieldset className="usage-ledger-fieldset">
                                                <legend>Direction</legend>
                                                <label className="usage-ledger-radio" htmlFor="usage-adjust-kind-compensate">
                                                    <input
                                                        id="usage-adjust-kind-compensate"
                                                        type="radio"
                                                        name="usage-adjust-kind"
                                                        value="compensate"
                                                        checked={form.kind === 'compensate'}
                                                        onChange={() => updateForm({ kind: 'compensate', grantId: '' })}
                                                    />
                                                    Compensate (add points)
                                                </label>
                                                <label className="usage-ledger-radio" htmlFor="usage-adjust-kind-clawback">
                                                    <input
                                                        id="usage-adjust-kind-clawback"
                                                        type="radio"
                                                        name="usage-adjust-kind"
                                                        value="clawback"
                                                        checked={form.kind === 'clawback'}
                                                        onChange={() => updateForm({ kind: 'clawback' })}
                                                    />
                                                    Clawback (take points back, bounded by one grant)
                                                </label>
                                            </fieldset>

                                            <div className="usage-ledger-field">
                                                <label htmlFor="usage-adjust-amount">Amount (points)</label>
                                                <input
                                                    id="usage-adjust-amount"
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={form.points}
                                                    onChange={(e) => updateForm({ points: e.target.value })}
                                                    placeholder="500"
                                                    autoComplete="off"
                                                />
                                                <small>
                                                    {amountValid
                                                        ? `${amountMilli} milli on the wire.`
                                                        : 'A positive number of points. The direction above carries the sign.'}
                                                </small>
                                            </div>

                                            {isClawback ? (
                                                <div className="usage-ledger-field">
                                                    <label htmlFor="usage-adjust-grant">Target grant</label>
                                                    <select
                                                        id="usage-adjust-grant"
                                                        value={form.grantId}
                                                        onChange={(e) => updateForm({ grantId: e.target.value })}
                                                    >
                                                        <option value="">Choose a grant…</option>
                                                        {clawbackTargets.map((grant) => (
                                                            <option value={grant.id} key={grant.id}>
                                                                {`${grant.grantKind} — ${toPoints(grant.usageRemainingMilli)} points left — ${grant.id}`}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <small>
                                                        {clawbackTarget
                                                            ? `Bounded by this grant's remaining ${toPoints(clawbackTarget.usageRemainingMilli)} points.`
                                                            : 'A clawback takes points back from exactly one grant, and only one that still has a remaining balance.'}
                                                    </small>
                                                </div>
                                            ) : (
                                                <fieldset className="usage-ledger-fieldset">
                                                    <legend>Expiry</legend>
                                                    <label className="usage-ledger-radio" htmlFor="usage-adjust-expiry-none">
                                                        <input
                                                            id="usage-adjust-expiry-none"
                                                            type="radio"
                                                            name="usage-adjust-expiry"
                                                            value="none"
                                                            checked={form.expiryPolicy === 'none'}
                                                            onChange={() => updateForm({ expiryPolicy: 'none', expiresAtUtc: '' })}
                                                        />
                                                        No expiry (permanent, like purchased points)
                                                    </label>
                                                    <label className="usage-ledger-radio" htmlFor="usage-adjust-expiry-custom">
                                                        <input
                                                            id="usage-adjust-expiry-custom"
                                                            type="radio"
                                                            name="usage-adjust-expiry"
                                                            value="custom"
                                                            checked={form.expiryPolicy === 'custom'}
                                                            onChange={() => updateForm({ expiryPolicy: 'custom' })}
                                                        />
                                                        Expires on a custom UTC date
                                                    </label>
                                                    {form.expiryPolicy === 'custom' && (
                                                        <div className="usage-ledger-field" style={{ marginTop: 'var(--space-2)' }}>
                                                            <label htmlFor="usage-adjust-expires">Custom expiry date</label>
                                                            <input
                                                                id="usage-adjust-expires"
                                                                type="date"
                                                                value={form.expiresAtUtc}
                                                                onChange={(e) => updateForm({ expiresAtUtc: e.target.value })}
                                                            />
                                                            <small>Must be in the future; the server refuses anything else.</small>
                                                        </div>
                                                    )}
                                                </fieldset>
                                            )}

                                            <div className="usage-ledger-field">
                                                <label htmlFor="usage-adjust-reason">Reason (required)</label>
                                                <textarea
                                                    id="usage-adjust-reason"
                                                    value={form.reason}
                                                    onChange={(e) => updateForm({ reason: e.target.value })}
                                                    maxLength={MAX_REASON_LENGTH}
                                                    placeholder="e.g. Credit for the Sep 13 translation outage"
                                                    aria-describedby="usage-adjust-reason-hint"
                                                />
                                                {/* The server copies this text verbatim onto the tenant's own Activity page (Activity Log plan D4). */}
                                                <small id="usage-adjust-reason-hint">{`${MIN_REASON_LENGTH}-${MAX_REASON_LENGTH} characters. The customer reads this word for word on their Activity page, from "QolSync support". Write it to them; keep internal notes in the ticket.`}</small>
                                            </div>

                                            <div className="usage-ledger-field">
                                                <label htmlFor="usage-adjust-ticket">Ticket (required)</label>
                                                <input
                                                    id="usage-adjust-ticket"
                                                    type="text"
                                                    value={form.ticket}
                                                    onChange={(e) => updateForm({ ticket: e.target.value })}
                                                    placeholder="SUP-4821"
                                                    autoComplete="off"
                                                />
                                                <small>
                                                    Letters, digits, dot, underscore, colon or hyphen. This is the
                                                    ledger identity of the adjustment: repeating it applies the
                                                    adjustment once, whatever happens to the connection.
                                                </small>
                                            </div>

                                            <div className="usage-ledger-field">
                                                <label htmlFor="usage-adjust-idempotency">Idempotency key (generated)</label>
                                                <input
                                                    id="usage-adjust-idempotency"
                                                    type="text"
                                                    value={idempotencyKey}
                                                    readOnly
                                                    data-testid="usage-ledger-idempotency-key"
                                                />
                                                <small>Derived from the direction, ticket and amount, so a retry of the same figures carries the same key.</small>
                                            </div>

                                            <div className="usage-ledger-actions">
                                                <button
                                                    type="button"
                                                    className="usage-ledger-secondary-btn"
                                                    onClick={handlePreview}
                                                    disabled={!formValid || isPreviewing || isApplying}
                                                >
                                                    {isPreviewing ? 'Previewing…' : 'Preview'}
                                                </button>
                                            </div>
                                            {!withinRemaining && (
                                                <p className="usage-ledger-hint">
                                                    That is more than the chosen grant has left, and the server
                                                    refuses a clawback that would exceed it.
                                                </p>
                                            )}
                                        </div>

                                        <div className="usage-ledger-adjust-preview">
                                            {preview ? (
                                                <div className="usage-ledger-preview-box" data-testid="usage-ledger-preview">
                                                    <h4>Preview — nothing has been written</h4>
                                                    <dl className="usage-ledger-preview-rows">
                                                        <div>
                                                            <dt>Available before</dt>
                                                            <dd data-testid="preview-before">{`${toPoints(preview.before.availableMilli)} points`}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Adjustment</dt>
                                                            <dd data-testid="preview-delta">
                                                                {`${signedPoints((preview.affectedGrants && preview.affectedGrants[0] && preview.affectedGrants[0].deltaMilli) || 0)} points`}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt>Available after</dt>
                                                            <dd data-testid="preview-after">{`${toPoints(preview.after.availableMilli)} points`}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Grant</dt>
                                                            <dd><code>{(preview.affectedGrants && preview.affectedGrants[0] && preview.affectedGrants[0].grantId) || '-'}</code></dd>
                                                        </div>
                                                        <div>
                                                            <dt>Ticket</dt>
                                                            <dd>{ticketText}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Balance version sent</dt>
                                                            <dd>{String(balanceVersion)}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Actor</dt>
                                                            <dd>{actor || '-'}</dd>
                                                        </div>
                                                    </dl>
                                                    {(preview.warnings || []).length > 0 && (
                                                        <ul>
                                                            {preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                                        </ul>
                                                    )}
                                                    <div className="usage-ledger-actions" style={{ marginTop: 'var(--space-3)' }}>
                                                        <button
                                                            type="button"
                                                            className="usage-ledger-primary-btn"
                                                            onClick={handleConfirm}
                                                            disabled={isApplying || !formValid}
                                                        >
                                                            {isApplying ? 'Applying…' : 'Confirm adjustment'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="usage-ledger-preview-box">
                                                    <h4>Preview — nothing has been written</h4>
                                                    <p className="usage-ledger-empty" style={{ background: 'var(--bg-secondary)' }}>
                                                        Preview the adjustment to see the balance before and after it.
                                                        Confirm stays unavailable until then.
                                                    </p>
                                                    <div className="usage-ledger-actions" style={{ marginTop: 'var(--space-3)' }}>
                                                        <button
                                                            type="button"
                                                            className="usage-ledger-primary-btn"
                                                            onClick={handleConfirm}
                                                            disabled
                                                        >
                                                            Confirm adjustment
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {adjustError && (
                                                <div className="usage-ledger-notice usage-ledger-notice-error" role="alert" data-testid="usage-ledger-adjust-error">
                                                    <AlertCircle size={16} className="usage-ledger-notice-icon" style={{ color: 'var(--error)' }} aria-hidden="true" />
                                                    <span>{adjustError}</span>
                                                </div>
                                            )}

                                            {adjustResult && (
                                                <div
                                                    className={`usage-ledger-notice usage-ledger-notice-${adjustResult.alreadyProcessed ? 'warning' : 'success'}`}
                                                    role="status"
                                                    data-testid="usage-ledger-adjust-result"
                                                >
                                                    {adjustResult.alreadyProcessed
                                                        ? <AlertTriangle size={16} className="usage-ledger-notice-icon" style={{ color: 'var(--warning)' }} aria-hidden="true" />
                                                        : <CheckCircle size={16} className="usage-ledger-notice-icon" style={{ color: 'var(--success)' }} aria-hidden="true" />}
                                                    <span>
                                                        <strong>
                                                            {adjustResult.alreadyProcessed
                                                                ? 'Already processed — the balance did not move.'
                                                                : `Applied ${toPoints(adjustResult.appliedMilli)} points.`}
                                                        </strong>
                                                        {`Ticket ${adjustResult.ticketId} · operation `}
                                                        <code>{adjustResult.operationId}</code>
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {sessionAudit.length > 0 && (
                                        <>
                                            <h3 style={{ marginTop: 'var(--space-6)' }}>Adjustments applied from this browser session</h3>
                                            <p className="usage-ledger-hint">
                                                The durable record is the operation row in the table above. The
                                                server stores the reason and your admin id on it, but the table
                                                does not show them yet, so this list is what to paste back into
                                                the ticket before leaving the page.
                                            </p>
                                            <div className="usage-ledger-table-wrap" role="region" tabIndex={0} aria-label="This session's adjustments table, scrolls horizontally">
                                                <table className="usage-ledger-table usage-ledger-table-wide" data-testid="usage-ledger-session-audit">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">When (UTC)</th>
                                                            <th scope="col">Actor</th>
                                                            <th scope="col">Direction</th>
                                                            {/* Outcome sits BEFORE the points, and a replayed row says so in
                                                                its own points cell. A retry and the write it repeats are two
                                                                rows carrying the same figure, so an outcome column parked at
                                                                the far end of a horizontally scrolling table would let one
                                                                credit read as two. */}
                                                            <th scope="col">Outcome</th>
                                                            <th scope="col" className="usage-ledger-num">Points</th>
                                                            <th scope="col">Ticket</th>
                                                            <th scope="col">Operation</th>
                                                            <th scope="col">Idempotency key</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sessionAudit.map((entry) => (
                                                            <tr key={`${entry.operationId}-${entry.atUtc}`}>
                                                                <th scope="row">{formatUtc(entry.atUtc)}</th>
                                                                <td>{entry.actor}</td>
                                                                <td>{entry.kind}</td>
                                                                <td>{entry.alreadyProcessed ? 'Already processed' : 'Applied'}</td>
                                                                <td className="usage-ledger-num">
                                                                    {entry.alreadyProcessed
                                                                        ? `${toPoints(entry.appliedMilli)} (no new points)`
                                                                        : toPoints(entry.appliedMilli)}
                                                                </td>
                                                                <td>{entry.ticketId}</td>
                                                                <td><code>{entry.operationId}</code></td>
                                                                <td><code>{entry.idempotencyKey}</code></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </section>
    );
};

export default UsageLedgerSection;
